/**
 * MOCK/TEMPORARY — music generation and Studio processing.
 *
 * ============================================================================
 * NO MUSIC IS GENERATED. There is no model, no audio, and no server. A timer
 * resolves after a fixed delay and a Song record is fabricated locally with a
 * random length/BPM/mood. `audioUrl` is deliberately left null so that no code
 * downstream can mistake a placeholder for a playable track.
 *
 * The simulated delays below are NOT arbitrary — they are the prototype's
 * original timings, preserved so the perceived pacing of the UI is unchanged:
 *   song generation   1100ms
 *   studio action     1300ms
 *   DNA analysis      1300ms
 *   mixer step tick    320ms
 * ============================================================================
 *
 * GENERATION IS A JOB NOW, NOT A REQUEST THAT RETURNS A TRACK (Batch 7 §1.7). This module
 * used to collapse `POST /generations` and `GET /generations/:id` into one promise that
 * resolved with a finished Song after 1100ms, which is a shape no backend can implement:
 * music generation takes minutes, so the server accepts the request, answers with a job,
 * and the client polls. `GenerationJob` has described that flow in types/domain since the
 * beginning and had NO RUNTIME USERS — only doc comments pointing at it.
 *
 * The three functions below are the flow, and each is one endpoint:
 *
 *   createGenerationJob()  POST /generations      → a QUEUED job. No song exists yet.
 *   getGeneration(id)      GET  /generations/:id  → the job, as it now stands.
 *   createGeneration()     both, in a loop        → the finished Song, or a rejection.
 *
 * `createGeneration` keeps its exact old signature — `(input) => Promise<Song>` — because
 * every creation path in the app calls it and the contract predicted precisely this
 * ("when the real flow lands, this function becomes the poll loop and its signature does
 * not change", docs/api-contract.md §8). What changed is underneath: it now cannot report
 * a song until a job has actually reached `succeeded`, and there is no code path that
 * treats an accepted request as a finished one.
 *
 * THE SONG DOES NOT EXIST UNTIL THE JOB SUCCEEDS, and that is the substance of the change
 * rather than the protocol. api/mockGenerations holds the record it will produce out of
 * reach of every read; a pending or failed job puts nothing in the songs table, so nothing
 * can be fetched, published or shared before the (simulated) generator finished. A failed
 * job produces no song at all and rejects — see GENERATION_FAILED_STATUS.
 *
 * NO COVER IMAGE IS GENERATED EITHER, but the artwork is no longer arbitrary. A finished
 * song now carries `artworkSeed`, `coverStyle` and `coverSource`, derived from its own
 * genre, mood and prompt by lib/coverConcept — before this, it carried no artwork field at
 * all, so the gradient fell back to the song's UUID and the prompt influenced nothing.
 * `coverUrl` stays null because there is no image model; see
 * api/coverApi.describeCoverBlockers() for the list the Studio's panel renders.
 *
 * NO SONG IS ANALYSED EITHER. `analyzeMusicDna` accepts Spotify links and carries the
 * source into the request — that part is real and finished. What it cannot do is retrieve
 * the track: the profile is hashed from the URL string. `musicDnaSupport()` says so in a
 * form the UI can render, so the screen states the limitation rather than implying a
 * Spotify integration that never existed.
 *
 * Real endpoints: docs/api-contract.md, "Generation jobs" and "Studio actions".
 */
import { API_BASE_URL, USE_MOCK_API } from '../config';
import { DEFAULT_LANGUAGE, DNA_MOODS } from '../data/music';
import type { GenerationCreditSource } from '../lib/credits';
import { coverConceptFor } from '../lib/coverConcept';
import { formatTime } from '../lib/formatting';
import { newId } from '../lib/ids';
import { isGoogleContentPolicyError, userFacingGenerationError } from '../lib/googlePolicy';
import { MUSIC_SOURCES, analyzeMusicDNA } from '../lib/music';
import { generateLyrics } from '../lib/music';
import type { MusicGenerationModel } from '../lib/musicGenerationModels';
import { STEM_TRACKS } from '../lib/stems';
import type {
  GenerationJob,
  GenerationBonusPackRef,
  MusicDNA,
  MusicSource,
  Song,
  StemSplitJob,
  StudioAction,
  StudioExtendJob,
  StudioRemixJob,
  StudioReplaceJob,
  StudioActionJob,
} from '../types/domain';
import {
  IMAGE_MUSIC_MODEL,
  MAX_LYRIA_IMAGES,
  buildImageMusicPrompt,
  buildLyriaRequest,
  describeLyriaBlockers,
} from './lyria';
import type { LyriaBlocker, LyriaImage, LyriaRequest } from './lyria';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';
import { mockGeneration, mockGenerationEnqueue } from './mockGenerations';
import { songsApi } from './songsApi';
import { audioEditApi } from './audioEditApi';
import { waitForStudioAction } from './studioActionJobs';

/*
  Original simulated durations, in ms. Kept together so the pacing is auditable.

  `mixerStepTick: 320` used to be here, driving the Genre Mixer's four progress rows.
  It went with them: the rows advanced on that interval regardless of the request, which
  reported elapsed time as work completed. The mixer now shows the same processing state
  as every other creation path (components/studio/ProcessingWave), which claims only
  that something is happening.

  `generateSong: 1100` IS STILL THE TOTAL, and is now spent across the job's stages rather
  than in one `mockDelay`: the accept, three polls and the queue's own progression add up to
  exactly it (see GENERATION_JOB_TIMINGS and mockGenerations.MOCK_GENERATION_STAGES), so the
  perceived pacing of every creation path is unchanged by §1.7. It is kept here, no longer
  passed to any `mockDelay`, as the figure those two tables are derived from — and the sum is
  asserted in api/__tests__/generationJob.test.ts so the derivation cannot quietly drift.
*/
export const SIMULATED_TIMINGS = {
  generateSong: 1100,
  studioAction: 1300,
  analyzeDna: 1300,
} as const;

/**
 * Latency of the two generation endpoints, and how often the client polls.
 *
 * SEPARATE FROM THE QUEUE'S OWN CLOCK, which lives in mockGenerations. These are round-trip
 * times — what a request costs — and those are stages a job passes through on the server
 * whether or not anyone is asking. Splitting them is what makes the poll loop honest: a poll
 * observes whatever stage the job has reached by the time it lands, so the client learns
 * nothing it was not told, and `pollInterval` is the client's decision rather than a number
 * the mock arranges to line up with a transition.
 *
 * `create + finished + poll` lands at SIMULATED_TIMINGS.generateSong, so a generation still
 * takes about 1100ms end to end. Nothing on screen moves at that speed regardless: every
 * creation path holds its processing state for PROCESSING_MS (lib/processing.ts), five
 * seconds, which is always the longer of the two.
 */
export const GENERATION_JOB_TIMINGS = {
  /** POST /generations — accepting the request. */
  create: 200,
  /** GET /generations/:id — one poll. */
  poll: 120,
  /** How long the client waits between polls in mock mode. */
  pollInterval: 180,
} as const;

export const BACKEND_GENERATION_POLL_INTERVAL_MS = 3000;
export const BACKEND_STUDIO_REPLACE_POLL_INTERVAL_MS = 3000;
export const MAX_STUDIO_REPLACE_POLLS = 200;

type StemAvailabilityListener = (availability: Awaited<ReturnType<typeof audioEditApi.getStemAvailability>>) => void;

async function waitForStemSplit(
  songId: string,
  accepted: StemSplitJob,
  onAvailability?: StemAvailabilityListener,
  sourceVersionId?: string
): Promise<void> {
  for (let poll = 0; poll < MAX_STUDIO_REPLACE_POLLS; poll += 1) {
    const availability = await withGenerationRetries(() =>
      audioEditApi.getStemAvailability(songId, sourceVersionId)
    );
    onAvailability?.(availability);
    const job = availability.job;
    if (!job || job.id !== accepted.id) {
      throw new ApiError(404, `Stem split job ${accepted.id} is no longer known to the server.`);
    }
    if (job.status === 'failed') {
      throw new ApiError(502, job.error ?? 'Stem separation failed.', 'server_error');
    }
    if (job.status === 'succeeded' && availability.stemTypes.length >= job.stemNames.length) return;
    await sleep(BACKEND_STUDIO_REPLACE_POLL_INTERVAL_MS);
  }
  throw new ApiError(
    504,
    'Stem separation is taking longer than expected. It is still running and will resume when you return.',
    'server_error'
  );
}

async function resumeExistingStemSplit(
  songId: string,
  onAvailability?: StemAvailabilityListener,
  sourceVersionId?: string
): Promise<boolean> {
  if (USE_MOCK_API) return false;
  const availability = await withGenerationRetries(() =>
    audioEditApi.getStemAvailability(songId, sourceVersionId)
  );
  onAvailability?.(availability);
  if (
    availability.job?.status === 'succeeded' &&
    availability.stemTypes.length >= availability.job.stemNames.length
  ) return true;
  if (!availability.job && availability.stemTypes.length >= STEM_TRACKS.length) return true;
  if (!availability.job || availability.job.status === 'failed') return false;
  await waitForStemSplit(songId, availability.job, onAvailability, sourceVersionId);
  return true;
}

/**
 * How many polls a simulated generation takes: `create + 3 × (pollInterval + poll)` = 1100ms,
 * which is SIMULATED_TIMINGS.generateSong exactly.
 *
 * Exported for the test that pins that arithmetic. It is not read by the poll loop — the
 * loop discovers the job's state by asking, which is the entire point, and a loop that knew
 * in advance how many polls a job takes would be a countdown wearing a poll loop's clothes.
 */
export const SIMULATED_GENERATION_POLLS = 3;

/**
 * How many polls `createGeneration` will make before giving up.
 *
 * A CEILING, NOT A DEADLINE, and it exists so a job that never progresses cannot hang the
 * caller for ever. It is deliberately generous against the mock's own stage timings — the
 * simulated job finishes in about four polls — because a poll loop that gives up while the
 * work is still running would report a failure that did not happen, which is the same class
 * of lie as reporting a success that did not happen.
 *
 * The real backend's figure will be much larger (a minutes-long generation at a 200ms
 * interval), and that is a number for whoever implements the endpoint to set against
 * measured job durations. See GENERATION_TIMEOUT_MESSAGE for what the user is told.
 */
export const MAX_GENERATION_POLLS = 200;

/** How many total attempts a transiently failing generation request gets. */
export const GENERATION_RETRY_ATTEMPTS = 3;
/** Base wait before the next retry, in ms. */
export const GENERATION_RETRY_DELAY_MS = 350;

/**
 * What a caller is told when the job never reached a terminal state.
 *
 * IT DOES NOT SAY THE GENERATION FAILED, because nobody knows that: the job may still be
 * running on a server that this client stopped asking. "We stopped waiting" is the true
 * statement, and it is materially different from "your song could not be made" — one invites
 * checking again, the other invites starting over.
 */
export const GENERATION_TIMEOUT_MESSAGE =
  'The generation is taking longer than expected. It may still be running — check your ' +
  'library in a moment.';

export interface TextGenerationSupport {
  available: boolean;
  blockers: readonly { id: 'no-backend'; summary: string; resolution: string }[];
}

export interface CreateGenerationInput {
  prompt: string;
  genre: string;
  /** Opaque claimed bonus entries. Only the backend resolves their hidden instructions. */
  bonusPacks?: readonly GenerationBonusPackRef[];
  /**
   * Vocal treatment, e.g. "Male vocal" / "Instrumental only".
   *
   * The composer has always collected this and the documented request body has
   * always carried it (docs/api-contract.md, POST /generations), but nothing
   * forwarded it — the picker changed state and then the value was dropped, so
   * choosing "Instrumental only" produced a song with lyrics. It is optional here
   * because the Genre Mixer and Music DNA generate without asking for it.
   */
  vocal?: string;
  /**
   * Language the vocals should be in, e.g. "English" / "Spanish".
   *
   * The documented request body has always carried it (docs/api-contract.md, POST
   * /generations) and the Song model has always had the field; what was missing was a
   * way for the user to say. The composer collects it now (data/music.LANGUAGES).
   *
   * Optional, and defaulted below rather than required, because the Genre Mixer and
   * Music DNA reach this through the same funnel and state the language inside their
   * generated prompt instead of as a field.
   */
  language?: string;
  /** Provider model selected in the Composer. The server validates paid-model access. */
  model?: MusicGenerationModel;
  /** Creation surface used by the backend to select the authoritative credit charge. */
  source?: GenerationCreditSource;
  /** Client/mock display cost. The real backend derives its charge from `source`. */
  cost: number;
  /** Ordering key assigned by the caller (the songs store owns the counter). */
  seq: number;
  /**
   * Who is generating — the signed-in account, for attributing the finished song.
   *
   * ============================================================================
   * NOT PART OF THE REAL REQUEST BODY, AND MUST NEVER BECOME PART OF IT.
   *
   * `POST /generations` derives the creator from the `Authorization` header, never from
   * the payload: a body field naming the creator is a field a client can lie about, and
   * a client that can name any creator can publish a track attributed to someone else's
   * account. So this is here to stand in for what the *response* will carry, not to be
   * sent — the real implementation of this function drops this parameter and reads
   * `creatorId`/`creatorName` off the returned record.
   *
   * That is why it is a nested object with the two fields the Song carries rather than
   * a `userId: string`: it is shaped like the answer, not like a request argument, so
   * the deletion is obvious when it comes.
   * ============================================================================
   *
   * OPTIONAL, so a caller with no session generates an unattributed song rather than
   * one credited to nobody-in-particular. See lib/attribution for why absent is a real
   * answer and no placeholder is substituted.
   */
  creator?: { id: string; name: string };
  /**
   * Reference images the music should be inspired by — the Image Music path.
   *
   * AN ARRAY, AND NOT A SINGLE IMAGE, because the API takes up to ten (see api/lyria.ts)
   * and a `image?: LyriaImage` field here would put our own ceiling of one below
   * Google's ceiling of ten for no reason other than that today's UI collects one at a
   * time. The UI can grow into this without the boundary changing.
   *
   * Optional: the composer, the Genre Mixer and Music DNA generate from text alone and
   * pass nothing here, so their calls are unchanged.
   */
  images?: readonly LyriaImage[];
}

/**
 * One reason the frontend cannot offer Image Music yet.
 *
 * Same record shape as the other support lists in this file, and deliberately so: the UI
 * already knows how to render `{ summary, resolution }` lists and this keeps Image Music
 * aligned with that pattern without reusing the lower-level Lyria blocker ids whose job is
 * to describe direct browser-to-Google calls.
 */
export interface ImageMusicBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-backend';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * Everything about a generation that only Image Music has to say.
 *
 * Its own type so `imageMusicSupport()` can be asserted, and so the UI has one value to
 * render rather than a set of booleans it has to keep consistent with this file.
 */
export interface ImageMusicSupport {
  /** True when the frontend can send image-backed generations to the Lyrixa backend. */
  available: boolean;
  /** The exact model an Image Music request targets once the backend forwards it. */
  model: string;
  /** The documented per-request image ceiling. */
  maxImages: number;
  /** Why `available` is false, in the order it would have to be fixed. */
  blockers: ImageMusicBlocker[];
}

/**
 * One reason the Music DNA analysis cannot read the track behind a pasted link.
 *
 * Same record shape as api/lyria.LyriaBlocker, and deliberately so: the UI already knows
 * how to render a list of `{ summary, resolution }`, and a second shape for the same idea
 * would be a second thing to keep in step. Its own type rather than a reuse of
 * LyriaBlocker because the ids are different facts — these are about the analysis
 * endpoint, not about Lyria 3.
 */
export interface MusicDnaBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-backend';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * Everything the Music DNA path can and cannot do, per source.
 *
 * WHY THIS EXISTS. The screen's copy needs to come from the boundary that knows rather
 * than from a sentence in the component. `sources` is the accept-list and it is REAL:
 * Spotify links are recognised, routed and carried into the request. `analysisAvailable`
 * is the other half and it is false until a backend exists.
 */
export interface MusicDnaSupport {
  /** Sources whose links are recognised and accepted by the frontend. */
  sources: readonly MusicSource[];
  /** Sources with a real backend path today. */
  supportedSources: readonly MusicSource[];
  /** Sources that are recognised but not yet implemented server-side. */
  unsupportedSources: readonly MusicSource[];
  /** True when at least one accepted source has a real backend path. */
  analysisAvailable: boolean;
  /** Why some or all accepted sources are still unavailable. */
  blockers: MusicDnaBlocker[];
}

/**
 * Everything standing between a pasted link and the real track behind it.
 *
 * Computed, like describeLyriaBlockers(), so the entry disappears on its own the day
 * `VITE_API_BASE_URL` is set rather than needing an edit here and another in the UI.
 */
export function describeMusicDnaBlockers(): MusicDnaBlocker[] {
  if (!API_BASE_URL) {
    return [
      {
        id: 'no-backend',
        summary: 'No Lyrixsa backend is configured, so Spotify Music DNA cannot run yet.',
        resolution:
          'Set VITE_API_BASE_URL and implement POST /analysis/music-dna server-side — see ' +
          'docs/api-contract.md §16, which already documents the request and response this ' +
          'app sends and expects.',
      },
    ];
  }

  return [];
}

/**
 * Thrown when a generation genuinely cannot be performed, rather than simulated.
 *
 * WHY THIS EXISTS AND WHY IT IS NOT A FALLBACK. Everything else in this module answers
 * with a fabricated Song, which is honest for text prompts because the whole app is
 * openly a prototype and `audioUrl` is null so nothing downstream can mistake it for
 * audio. Image Music is different: it was asked for as a REAL Lyria 3 flow, so
 * returning a locally invented record would be a fake success for a feature whose
 * entire point is the actual API call. Refusing is the accurate outcome, and this error
 * carries the reason to the UI so it can be read rather than guessed.
 */
export class GenerationUnavailableError extends Error {
  readonly name = 'GenerationUnavailableError';
  /** What would have to change, from api/lyria.describeLyriaBlockers(). */
  readonly blockers: LyriaBlocker[];
  /** The request that WAS built and would have been sent. Useful in a bug report. */
  readonly request: LyriaRequest | null;

  constructor(message: string, blockers: LyriaBlocker[], request: LyriaRequest | null = null) {
    super(message);
    this.blockers = blockers;
    this.request = request;
  }
}

/**
 * Thrown when a generation job reached `failed` — the provider did not produce a track.
 *
 * ============================================================================
 * A REAL FAILURE, AND THE OPPOSITE OF GenerationUnavailableError. That one means "this
 * build cannot even make the request"; this one means the request was made, accepted, and
 * the work did not come out. Two different sentences for the user and two different
 * reactions, so two classes.
 *
 * AN `ApiError` SUBCLASS, and deliberately: a failed job is what a real backend reports as
 * a status, so it belongs in the app's one error model rather than beside it. `502` with
 * `server_error` puts it on the `server` branch — see api/errors.ApiErrorKind — which is
 * the branch that says retrying is reasonable, because it is: the same prompt may well
 * succeed on the next attempt. Nothing in api/errors needed changing for this; the class
 * is already an ApiError, so `toApiError` returns it untouched with its message intact.
 *
 * `job` is carried so a caller can report the id, which is the one thing that makes a
 * generation failure traceable in a backend's logs.
 * ============================================================================
 */
export class GenerationFailedError extends ApiError {
  /*
    A CLASS FIELD, not a constructor assignment, so it initialises AFTER `super()` has set
    `name = 'ApiError'` and therefore wins. The same pattern the other refusal classes in
    src/api use, and for the same reason: api/errors matches this app's own error classes by
    name rather than by `instanceof`.
  */
  readonly name = 'GenerationFailedError';
  /** The job as it finally stood, including its `error`. */
  readonly job: GenerationJob;

  constructor(job: GenerationJob) {
    const fallback = 'The generation did not produce a track. Nothing was created.';
    const blocked = isGoogleContentPolicyError(job.error ?? '');
    super(
      blocked ? 422 : 502,
      userFacingGenerationError(job.error, fallback),
      blocked ? 'validation_failed' : 'server_error'
    );
    this.job = job;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryGenerationError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.kind === 'network') return true;
  if (error.status === 429) return true;
  return error.kind === 'server';
}

async function withGenerationRetries<T>(run: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < GENERATION_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      lastError = error;
      if (!shouldRetryGenerationError(error) || attempt === GENERATION_RETRY_ATTEMPTS - 1) {
        throw error;
      }
      await sleep(GENERATION_RETRY_DELAY_MS * (attempt + 1));
    }
  }
  throw lastError;
}

async function getStudioReplaceJobStatus(
  songId: string,
  jobId: string
): Promise<StudioReplaceJob | null> {
  try {
    return await http.get<StudioReplaceJob>(`/songs/${songId}/studio/replace/${jobId}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

async function waitForStudioReplace(songId: string, accepted: StudioReplaceJob): Promise<Song> {
  for (let poll = 0; poll < MAX_STUDIO_REPLACE_POLLS; poll += 1) {
    await sleep(BACKEND_STUDIO_REPLACE_POLL_INTERVAL_MS);
    const job = await getStudioReplaceJobStatus(songId, accepted.id);
    if (!job) {
      throw new ApiError(
        404,
        `Studio replace job ${accepted.id} is no longer known to the server.`
      );
    }
    if (job.status === 'failed') {
      throw new ApiError(502, job.error ?? 'Studio replace failed.', 'server_error');
    }
    if (job.status !== 'succeeded') continue;
    if (!job.resultSongId) {
      throw new ApiError(
        502,
        `Studio replace job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    const song = await songsApi.getSong(job.resultSongId);
    if (!song) {
      throw new ApiError(
        502,
        `Studio replace job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    return song;
  }

  throw new ApiError(
    504,
    'The replace is taking longer than expected. It may still be running — check your library in a moment.',
    'server_error'
  );
}

async function getStudioExtendJobStatus(
  songId: string,
  jobId: string
): Promise<StudioExtendJob | null> {
  try {
    return await http.get<StudioExtendJob>(`/songs/${songId}/studio/extend/${jobId}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

async function waitForStudioExtend(songId: string, accepted: StudioExtendJob): Promise<Song> {
  for (let poll = 0; poll < MAX_STUDIO_REPLACE_POLLS; poll += 1) {
    await sleep(BACKEND_STUDIO_REPLACE_POLL_INTERVAL_MS);
    const job = await getStudioExtendJobStatus(songId, accepted.id);
    if (!job) {
      throw new ApiError(404, `Studio extend job ${accepted.id} is no longer known to the server.`);
    }
    if (job.status === 'failed') {
      throw new ApiError(502, job.error ?? 'Studio extend failed.', 'server_error');
    }
    if (job.status !== 'succeeded') continue;
    if (!job.resultSongId) {
      throw new ApiError(
        502,
        `Studio extend job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    const song = await songsApi.getSong(job.resultSongId);
    if (!song) {
      throw new ApiError(
        502,
        `Studio extend job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    return song;
  }

  throw new ApiError(
    504,
    'The extend is taking longer than expected. It may still be running — check your library in a moment.',
    'server_error'
  );
}

async function getStudioRemixJobStatus(
  songId: string,
  jobId: string
): Promise<StudioRemixJob | null> {
  try {
    return await http.get<StudioRemixJob>(`/songs/${songId}/studio/remix/${jobId}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

async function waitForStudioRemix(songId: string, accepted: StudioRemixJob): Promise<Song> {
  for (let poll = 0; poll < MAX_STUDIO_REPLACE_POLLS; poll += 1) {
    await sleep(BACKEND_STUDIO_REPLACE_POLL_INTERVAL_MS);
    const job = await getStudioRemixJobStatus(songId, accepted.id);
    if (!job) {
      throw new ApiError(404, `Studio remix job ${accepted.id} is no longer known to the server.`);
    }
    if (job.status === 'failed') {
      throw new ApiError(502, job.error ?? 'Studio remix failed.', 'server_error');
    }
    if (job.status !== 'succeeded') continue;
    if (!job.resultSongId) {
      throw new ApiError(
        502,
        `Studio remix job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    const song = await songsApi.getSong(job.resultSongId);
    if (!song) {
      throw new ApiError(
        502,
        `Studio remix job ${job.id} reported success but no song was returned.`,
        'server_error'
      );
    }
    return song;
  }

  throw new ApiError(
    504,
    'The remix is taking longer than expected. It may still be running — check your library in a moment.',
    'server_error'
  );
}

/**
 * Builds the exact Lyria 3 body an Image Music generation would send.
 *
 * A module function rather than only a method, and both `generationApi` members below
 * call THIS instead of `this.…`. Deliberate: a method that reaches through `this` breaks
 * the moment a caller destructures it (`const { createGeneration } = generationApi`),
 * and this codebase has already been bitten by exactly that once. Nothing here needs a
 * receiver, so nothing here asks for one.
 */
function describeTextGenerationBlockers(): TextGenerationSupport['blockers'] {
  if (API_BASE_URL) return [];
  return [
    {
      id: 'no-backend',
      summary:
        'Text song generation needs the Lyrixsa backend with a real audio provider; this frontend never generates tracks on its own.',
      resolution:
        'Configure VITE_API_BASE_URL and a backend generation provider so POST /generations can create and store real audio.',
    },
  ];
}

function describeImageMusicBlockers(): ImageMusicBlocker[] {
  if (API_BASE_URL) return [];
  return [
    {
      id: 'no-backend',
      summary:
        'Image Music needs the Lyrixsa backend before this screen can submit an image-backed generation.',
      resolution:
        'Configure VITE_API_BASE_URL so the frontend can send POST /generations to the backend that owns the Google Lyria 3 credentials.',
    },
  ];
}

function buildImageMusicRequestFor(input: CreateGenerationInput): LyriaRequest {
  const images = input.images ?? [];
  return buildLyriaRequest({
    model: input.model ?? IMAGE_MUSIC_MODEL,
    prompt: buildImageMusicPrompt({
      prompt: input.prompt,
      imageCount: images.length,
      genre: input.genre,
      vocal: input.vocal,
      language: input.language,
    }),
    images,
  });
}

export const generationApi = {
  resumeExistingStemSplit,
  /** What plain-text generation can actually do right now. */
  textGenerationSupport(): TextGenerationSupport {
    const blockers = describeTextGenerationBlockers();
    return { available: blockers.length === 0, blockers };
  },

  /**
   * What the Image Music path can actually do right now.
   *
   * Read by the UI so the screen's copy comes from the boundary that knows, rather than
   * from a hardcoded sentence in a component that will not be updated the day a backend
   * appears. Same idea as audioEditApi.supports().
   *
   * This answers a FRONTEND question: can this build submit an image-backed generation to
   * the Lyrixa backend? It deliberately does not try to prove that the backend is itself
   * fully configured with Google credentials, because the frontend has no truthful way to
   * know that without a separate capability endpoint. That later refusal still surfaces
   * fail-fast from POST /generations.
   */
  imageMusicSupport(): ImageMusicSupport {
    const blockers = describeImageMusicBlockers();
    return {
      available: blockers.length === 0,
      model: IMAGE_MUSIC_MODEL,
      maxImages: MAX_LYRIA_IMAGES,
      blockers,
    };
  },

  /**
   * Builds the exact Lyria 3 body an Image Music generation would send.
   *
   * Exposed so it is testable and inspectable: this is the part of the feature that is
   * genuinely finished, and being able to assert the model id and the multimodal block
   * structure is what distinguishes a real integration from an intention. `createGeneration`
   * builds it the same way, so what the tests check is what would go on the wire.
   */
  buildImageMusicRequest(input: CreateGenerationInput): LyriaRequest {
    return buildImageMusicRequestFor(input);
  },

  /**
   * MOCK: POST /generations → 202 with a QUEUED job
   *
   * ==========================================================================
   * THE REQUEST IS ACCEPTED, AND NOTHING HAS BEEN MADE. That is the whole contract of this
   * function, and it is what §1.7 asks for: the job comes back `status: 'queued'`,
   * `progress: 0` and with NO `songId`, because the song does not exist. There is nothing
   * here a caller could mistake for a finished track — not a Song, not an id, not a URL.
   *
   * The record the job will eventually produce IS fabricated here, since that is where the
   * lyrics, the cover concept and the (random) BPM are documented, and it is handed
   * straight to api/mockGenerations, which withholds it until the job succeeds. It is
   * deliberately NOT written into the songs table on the way past: a pending generation
   * that put a row in the table would be a song in the user's library that nothing had
   * generated yet, and it could be published from there.
   * ==========================================================================
   *
   * IMAGES TAKE A DIFFERENT PATH, a few lines down: a request carrying images is a real
   * Lyria 3 request or it is nothing, so it is built and then refused rather than
   * simulated. See GenerationUnavailableError. That refusal happens BEFORE a job exists,
   * which is correct — a request that cannot be dispatched was never accepted.
   */
  async createGenerationJob(
    input: CreateGenerationInput,
    options?: { idempotencyKey?: string }
  ): Promise<GenerationJob> {
    if (!USE_MOCK_API) {
      return withGenerationRetries(() =>
        http.post<GenerationJob>(
          '/generations',
          {
            prompt: input.prompt,
            genre: input.genre,
            vocal: input.vocal,
            language: input.language,
            ...(input.model ? { model: input.model } : null),
            ...(input.source ? { source: input.source } : null),
            seq: input.seq,
            ...(input.images ? { images: input.images } : null),
            ...(input.bonusPacks ? { bonusPacks: input.bonusPacks } : null),
          },
          options?.idempotencyKey
            ? { headers: { 'Idempotency-Key': options.idempotencyKey } }
            : undefined
        )
      );
    }
    assertMock('generationApi.createGenerationJob');
    const { prompt, genre, vocal, language, cost, seq, creator } = input;

    /*
      Image Music in mock mode. The request is built first, deliberately — building it
      validates the prompt and the image count against the documented limits, and the
      resulting body is attached to the error, so the thing that fails is the dispatch and
      not the integration. Everything up to `fetch` is real and exercised by tests.

      Then it refuses, because it must: mock mode has no backend to post to. Simulating a
      Song here would be the one thing the brief rules out — a feature that looks like it
      called Lyria 3 and did not. Runtime/backend mode takes the real POST /generations path
      above; this refusal is specifically the test-fixture branch.
    */
    if (input.images && input.images.length > 0) {
      const request = buildImageMusicRequestFor(input);
      const blockers = describeLyriaBlockers();
      if (blockers.length > 0) {
        throw new GenerationUnavailableError(
          `Image Music needs the Lyria 3 API (${IMAGE_MUSIC_MODEL}), and this build cannot reach it. ` +
            blockers.map((b) => b.summary).join(' '),
          blockers,
          request
        );
      }
      /*
        Unreachable while `describeLyriaBlockers()` always reports the credentials
        blocker, and left as a throw rather than an implementation because the real call
        belongs to the backend: the frontend's half is `request` above. assertMock at the
        top of this function already fails once VITE_API_BASE_URL is set, which is the
        signal to implement POST /generations for real — see docs/api-contract.md §8.
      */
      throw new GenerationUnavailableError(
        'A Lyrixsa backend is configured, but POST /generations does not yet forward images to Lyria 3.',
        [],
        request
      );
    }

    // Fabricated attributes. Random on purpose — a real generator reports these.
    const length = formatTime(120 + Math.floor(Math.random() * 60));
    /*
      The mood, chosen before the Song is built rather than inline in it, because the cover
      art needs it too — see the artwork fields below. Random for the same reason the BPM
      is: a real generator reports what it made.
    */
    const mood = DNA_MOODS[Math.floor(Math.random() * DNA_MOODS.length)];
    /*
      AUTOMATIC COVER ART. Every generated song now arrives with a visual direction derived
      from its own genre, mood and prompt (lib/coverConcept), instead of with no artwork
      field at all — which is what it had before, so the gradient fell back to the song's
      UUID via lib/artwork.artworkSeedFor and the prompt influenced nothing.

      SIMULATED, and the fields say which part: `coverUrl` is absent because there is no
      image model (api/coverApi.describeCoverBlockers), so what renders is still the
      gradient stand-in. What changed is that the seed is a hash of the direction, so two
      songs from the same prompt share a cover and two different prompts do not.
      `coverSource: 'generated'` is what makes every surface caption it "Placeholder
      artwork" rather than implying a picture.

      No variant is passed, deliberately: automatic artwork must be reproducible from the
      request, which is what makes "this cover came from this prompt" checkable. Only the
      Studio's Regenerate asks for a different take.
    */
    const concept = coverConceptFor({ prompt, genre, mood });
    const song: Song = {
      id: newId(),
      seq,
      /*
        WHO MADE IT, spread rather than assigned, so a generation with no session
        produces a song with NO creator fields at all rather than two undefined ones.
        The difference is not cosmetic: `'creatorId' in song` then answers "is this
        attributed?" honestly, and nothing downstream has to distinguish an absent field
        from a present-but-undefined one. lib/attribution renders nothing in that case,
        which is the correct outcome — see its header.

        THE REAL ENDPOINT SETS THESE FROM THE SESSION, not from anything the client sent.
        See `CreateGenerationInput.creator` above for why this must not become a request
        field, and docs/api-contract.md §6.
      */
      ...(creator ? { creatorId: creator.id, creatorName: creator.name } : null),
      title: prompt.split(' ').slice(0, 3).join(' ') || 'Untitled',
      genre,
      length,
      date: 'Just now',
      createdAt: new Date().toISOString(),
      lyrics:
        genre === 'Lo-fi instrumental' || vocal === 'Instrumental only'
          ? ''
          : generateLyrics(prompt, genre),
      prompt,
      favorite: false,
      /*
        GENERATION SETS THE STATUS AND NOTHING ELSE ABOUT WHO CAN SEE IT.

        `status: 'ready'` because the (simulated) generator finished. `visibility:
        'private'` because finishing is not publishing: the user asked for a song, not for
        a song on a public catalogue, and nothing in this request expressed a wish to
        publish. A future Publish action is the only thing that may change the second
        field — see types/domain.SongVisibility and docs/api-contract.md §22.

        The real POST /generations must behave identically. A backend that returned
        `visibility: 'public'` from a generation would publish everyone's tracks silently,
        and the frontend filtering them afterwards would not undo that.
      */
      status: 'ready',
      visibility: 'private',
      bpm: 70 + Math.floor(Math.random() * 90),
      mood,
      /*
        The requested language, recorded on the song.

        An instrumental request still overrules it, and in that order deliberately: a
        track with no vocals has no language to be in, so "Instrumental" is the accurate
        value and a request for Instrumental vocals in Japanese is not a contradiction to
        resolve in the user's favour — it is a field that no longer applies. The
        composer's picker offers no Instrumental option for the same reason.

        Otherwise the user's choice, falling back to DEFAULT_LANGUAGE for the callers
        that do not collect one (the Genre Mixer and Music DNA state the language inside
        their prompt). That fallback is the value this line always produced before the
        field existed, so nothing changes for a caller that stays silent.
      */
      language:
        genre === 'Lo-fi instrumental' || vocal === 'Instrumental only'
          ? 'Instrumental'
          : (language ?? DEFAULT_LANGUAGE),
      aiModel: 'Lyrixsa v1',
      creditsUsed: cost,
      songOrigin: 'generated',
      downloadCount: 0,
      // Explicitly null: there is no audio. See the header.
      audioUrl: null,
      /*
        Explicitly null for the same reason, and this is the one that would be easiest to
        fake: an image model returns a URL here, and until one is connected there is no
        honest value but null. `artworkSeed` beside it is what draws the gradient.
      */
      coverUrl: null,
      artworkSeed: concept.seed,
      coverStyle: concept.style,
      coverSource: 'generated',
      versions: [
        { id: newId(), label: 'Original', kind: 'original', date: 'Just now', length, prompt },
      ],
    };
    /*
      THE JOB, AND IT IS THE ONLY THING THAT LEAVES THIS FUNCTION.

      `queued`, `progress: 0`, no `songId` — the three fields that say "accepted, and not
      done". The song above goes to api/mockGenerations, which holds it out of reach of every
      read until the job reaches `succeeded` and then writes it into the songs table (Batch 7
      §1.6, api/mockCatalogue — the public reads query that table, so a song that is not in it
      cannot be published or shared). That ordering is the substance of §1.7: the record comes
      into existence when the generator finishes, not when the request is accepted.

      `id` is the JOB's id and not the song's. They are different records with different
      lifetimes, and conflating them is how a client ends up holding what it believes is a
      song id for a track that failed to generate.
    */
    const job: GenerationJob = {
      id: newId(),
      status: 'queued',
      prompt,
      genre,
      /*
        The empty string rather than `undefined` for a caller that did not choose: `vocal` is
        required on GenerationJob, and the two callers that do not collect one (the Genre
        Mixer and Music DNA) state it inside their prompt instead. A job is a record of a
        request, so it reports the request as made rather than substituting a default the
        user never picked — which is the same reasoning `CreateGenerationInput.vocal` is
        optional for.
      */
      vocal: vocal ?? '',
      // Zero, and it stays zero until the job finishes. Nothing here has progress to
      // report; see mockGenerations' header on why none is interpolated.
      progress: 0,
      createdAt: new Date().toISOString(),
    };
    mockGenerationEnqueue(job, song);
    return mockDelay(job, GENERATION_JOB_TIMINGS.create);
  },

  /**
   * MOCK: GET /generations/:id
   *
   * The job as it now stands. `null` for an id the server has never issued, which is the
   * `404` a real endpoint answers — returned rather than thrown for the reason
   * `shareApi.getShared` gives: a lookup that finds nothing is an answer, not a fault.
   *
   * WHAT A POLL MAY LEARN. A `succeeded` job carries `songId` and `progress: 100`; a
   * `failed` one carries `error` and NO `songId`, because a failed generation produced no
   * song. Nothing here reports a percentage between the two — see mockGenerations.
   */
  async getGeneration(id: string): Promise<GenerationJob | null> {
    if (!USE_MOCK_API) {
      try {
        return await withGenerationRetries(() => http.get<GenerationJob>(`/generations/${id}`));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    }
    assertMock('generationApi.getGeneration');
    return mockDelay(mockGeneration(id), GENERATION_JOB_TIMINGS.poll);
  },

  /**
   * MOCK: POST /generations, then poll GET /generations/:id until it settles.
   *
   * ==========================================================================
   * THE SAME SIGNATURE IT HAS ALWAYS HAD — `(input) => Promise<Song>` — and now the poll
   * loop the contract said it would become (docs/api-contract.md §8). Every creation path in
   * the app goes through hooks/useSongs.createSong to here, so keeping the signature is what
   * made §1.7 an API-layer change rather than a UI refactor.
   *
   * WHAT IT WILL NOT DO. It will not resolve because a request was accepted: a song is
   * returned only after a job reported `succeeded` AND the song it named was found. It does
   * not fabricate a Song from the input as a fallback, it does not invent an `audioUrl`, and
   * it does not treat a `failed` job as anything but a failure.
   *
   * THREE OUTCOMES, and each is honest about which one happened:
   *   succeeded  → the Song from the songs table, by the job's own `songId`
   *   failed     → GenerationFailedError, carrying the job and the provider's reason
   *   neither    → ApiError 504 after MAX_GENERATION_POLLS, saying we stopped waiting
   *                rather than that the generation failed
   *
   * A caller that wants the job itself — to render a queue position, or to survive a reload
   * mid-generation — calls `createGenerationJob` and `getGeneration` directly. Nothing does
   * yet, and that is stated rather than pre-built: the UI has no queue display to feed, and
   * §32 is explicit that an interface existing is not an implementation.
   * ==========================================================================
   */
  async createGeneration(input: CreateGenerationInput): Promise<Song> {
    const accepted = await generationApi.createGenerationJob(input, { idempotencyKey: newId() });

    for (let poll = 0; poll < MAX_GENERATION_POLLS; poll += 1) {
      /*
        WAITS BEFORE THE FIRST POLL, not after it. The job was `queued` a moment ago and
        asking again immediately would be a request whose answer is already known — which is
        free against a mock and is a wasted round trip against a server.
      */
      await (USE_MOCK_API
        ? mockDelay(undefined, GENERATION_JOB_TIMINGS.pollInterval)
        : sleep(BACKEND_GENERATION_POLL_INTERVAL_MS));
      const job = await generationApi.getGeneration(accepted.id);

      /*
        A job the server has forgotten. Not a timeout and not a failure: the id was issued
        and is now unknown, which a real backend answers 404 for, so that is what the caller
        is told rather than being left in the loop until the ceiling.
      */
      if (!job) {
        throw new ApiError(404, `Generation ${accepted.id} is no longer known to the server.`);
      }

      if (job.status === 'failed') throw new GenerationFailedError(job);

      if (job.status === 'succeeded') {
        /*
          THE SONG IS FETCHED, NOT ASSUMED. The job names an id; the record has to be found
          under it. A succeeded job with no `songId`, or one naming a row that is not there,
          is a broken response — and the only honest thing to do with it is say so, because
          the alternative is synthesising a Song from the request and calling it the result.
          Against the mock this cannot happen (mockGenerations writes the row before it sets
          the status); against a backend it is exactly the inconsistency worth catching.
        */
        if (!job.songId) {
          throw new ApiError(
            502,
            `Generation ${job.id} reported success but no song was returned.`,
            'server_error'
          );
        }

        const song = await songsApi.getSong(job.songId);
        if (!song) {
          throw new ApiError(
            502,
            `Generation ${job.id} reported success but no song was returned.`,
            'server_error'
          );
        }
        return song;
      }
      // 'queued' or 'running' — still working, so poll again.
    }

    /*
      The ceiling. 504 Gateway Timeout because that is what it is: the request stands, and
      this client stopped waiting for it. See GENERATION_TIMEOUT_MESSAGE on why the message
      does not claim the generation failed.
    */
    throw new ApiError(504, GENERATION_TIMEOUT_MESSAGE, 'server_error');
  },

  /**
   * MOCK: POST /songs/:id/studio/:action
   *
   * Takes the full typed StudioAction, which carries the extend/remix/replace/
   * cover prompt text. Those prompts are currently unused by the simulation but
   * are threaded through deliberately: Phase 5 forwards them to the real Studio
   * API, and the plumbing is in place now so that lands as a body change here
   * rather than a UI refactor.
   */
  async runStudioAction(
    action: StudioAction,
    options?: { onStemAvailability?: StemAvailabilityListener }
  ): Promise<Song | StudioAction | { ok: true; songId: string; action: StudioAction['kind'] }> {
    if (!USE_MOCK_API) {
      if (action.kind === 'replace') {
        const accepted = await http.post<StudioReplaceJob>(
          `/songs/${action.songId}/studio/replace`,
          {
            from: action.from,
            to: action.to,
            duration: action.duration,
            steps: action.steps,
            prompt: action.prompt,
          }
        );
        return waitForStudioReplace(action.songId, accepted);
      }
      if (action.kind === 'extend') {
        const accepted = await http.post<StudioExtendJob>(`/songs/${action.songId}/studio/extend`, {
          extend_from_s: action.extendFromS,
          extend_s: action.extendS,
          instruction: action.instruction,
          temperature: action.temperature,
          classifier_free_guidance: action.classifierFreeGuidance,
        });
        return waitForStudioExtend(action.songId, accepted);
      }
      if (action.kind === 'remix') {
        const accepted = await http.post<StudioRemixJob>(`/songs/${action.songId}/studio/remix`, {
          style: action.style,
          prompt: action.prompt,
          lyricsOverride: action.lyricsOverride,
        });
        return waitForStudioRemix(action.songId, accepted);
      }
      if (action.kind === 'stems' || action.kind === 'regenerate') {
        if (action.kind === 'stems') {
          const availability = await audioEditApi.getStemAvailability(action.songId, action.sourceVersionId);
          options?.onStemAvailability?.(availability);
          if (
            (availability.job?.status === 'succeeded' &&
              availability.stemTypes.length >= availability.job.stemNames.length) ||
            (!availability.job && availability.stemTypes.length >= STEM_TRACKS.length)
          ) {
            return { ok: true, songId: action.songId, action: action.kind };
          }
          const accepted =
            availability.job && availability.job.status !== 'failed'
              ? availability.job
              : await http.post<StemSplitJob>(`/songs/${action.songId}/studio/stems`, {
                  shifts: action.shifts,
                  ...(action.sourceVersionId ? { sourceVersionId: action.sourceVersionId } : null),
                });
          await waitForStemSplit(
            action.songId,
            accepted,
            options?.onStemAvailability,
            action.sourceVersionId
          );
          return { ok: true, songId: action.songId, action: action.kind };
        }
        const accepted = await http.post<StudioActionJob>(
          `/songs/${action.songId}/studio/${action.kind}`,
          { lyrics: action.lyrics }
        );
        return waitForStudioAction<Song>(action.songId, accepted);
      }
      return action;
    }
    assertMock('generationApi.runStudioAction');
    return mockDelay(action, SIMULATED_TIMINGS.studioAction);
  },

  /**
   * What the Music DNA path can actually do, and for which services.
   *
   * Read by the screen so its label, its placeholder and its notice come from the
   * boundary that knows, rather than from sentences in a component — which is how the
   * field came to say "Spotify song link" and nothing else long after the analysis had
   * stopped being about Spotify in particular. Same idea as imageMusicSupport().
   */
  musicDnaSupport(): MusicDnaSupport {
    const blockers = describeMusicDnaBlockers();
    return {
      sources: MUSIC_SOURCES,
      supportedSources: API_BASE_URL ? ['spotify'] : [],
      unsupportedSources: API_BASE_URL ? [] : MUSIC_SOURCES,
      analysisAvailable: API_BASE_URL !== '',
      blockers,
    };
  },

  /**
   * MOCK: POST /analysis/music-dna
   *
   * Hashes the URL rather than analysing audio, so the same link always yields
   * the same profile. Real analysis returns this same shape; see musicDnaSupport()
   * for what it would take, and docs/api-contract.md §16 for the endpoint.
   *
   * `source` IS PART OF THE REQUEST, and not merely recorded on the way past. It is what
   * a backend needs to know which catalogue to look the link up in, and it is passed
   * because that is the boundary the frontend can finish today: the app detects the
   * service, names it in the request, and a real implementation of this endpoint has
   * everything it needs on arrival. It changes nothing about the mock's output, which is
   * the point — a source that altered the hash would be two simulations pretending to be
   * two integrations.
   */
  async analyzeMusicDna(url: string, source?: MusicSource): Promise<MusicDNA> {
    if (!USE_MOCK_API) {
      return http.post<MusicDNA>('/analysis/music-dna', source ? { url, source } : { url });
    }
    assertMock('generationApi.analyzeMusicDna');
    return mockDelay(analyzeMusicDNA(url, source), SIMULATED_TIMINGS.analyzeDna);
  },
};
